import React, { useState } from "react";

// ────────────────────────────────────────────────────────────
// Joborigo — Agency-side CCR / Capability Map v2
// Phase 3 design reference mockup
//
// Key reframe from v1:
// - The capability template belongs to the ROLE, not the candidate
// - Multiple candidates are projected onto the same template
// - Cell = depth tier × evidence strength (dual representation)
// - Trajectory removed — that's a list-mode concern
// - Evidence drill-down on cell click
//
// Composite cell score = tier_points × (evidence_strength / 5)
//   tiers: foundational (1pt), applied (3pt), experienced (10pt)
//   evidence: 0-5 (peer endorsements, employment, certifications)
// ────────────────────────────────────────────────────────────

// Mock data

const ROLE = {
  title: "Senior Backend Engineer",
  client: "FinTech client A",
  template: "FinTech-Backend-Sr-v2",
};

// Capability template — role-defined, bottom-up
// Lower index = more foundational; higher index = more applied/experienced
const CAPABILITIES = [
  { id: "cap1", label: "Programming fundamentals", layer: "Foundation" },
  { id: "cap2", label: "Database design", layer: "Foundation" },
  { id: "cap3", label: "Backend systems", layer: "Applied" },
  { id: "cap4", label: "Distributed architecture", layer: "Applied" },
  { id: "cap5", label: "Payment systems", layer: "Domain" },
  { id: "cap6", label: "Compliance / regulatory", layer: "Domain" },
  { id: "cap7", label: "Team leadership", layer: "Experience" },
  { id: "cap8", label: "Production ownership", layer: "Experience" },
];

const TIERS = {
  basic: { label: "Basic", points: 1, segments: 1, abbrev: "B" },
  working: { label: "Working", points: 3, segments: 2, abbrev: "W" },
  extensive: { label: "Extensive", points: 6, segments: 3, abbrev: "X" },
  expert: { label: "Expert", points: 10, segments: 4, abbrev: "E" },
};

// Universal recency thresholds (in years from today)
// current: <1yr — full size
// recent: 1-5yr — medium size
// past: 5yr+ — smallest size
const RECENCY = {
  CURRENT_THRESHOLD_YEARS: 1,
  RECENT_THRESHOLD_YEARS: 5,
};

function recencyOfDate(isoDate) {
  if (!isoDate) return "past";
  const now = new Date("2026-05-21");
  const then = new Date(isoDate);
  const yearsAgo = (now - then) / (1000 * 60 * 60 * 24 * 365.25);
  if (yearsAgo < RECENCY.CURRENT_THRESHOLD_YEARS) return "current";
  if (yearsAgo < RECENCY.RECENT_THRESHOLD_YEARS) return "recent";
  return "past";
}

// Tier-bar size from the LATEST evidence date in the cell
function recencyOfCell(cell) {
  if (!cell || !cell.evidenceDates || cell.evidenceDates.length === 0)
    return "past";
  const latest = cell.evidenceDates.slice().sort().reverse()[0];
  return recencyOfDate(latest);
}

// Size classes for tier segments and evidence dots
// Strong size ratio (1:2:3) to make recency distinguishable at a glance
const SEGMENT_SIZE = {
  current: "w-1.5 h-6",
  recent: "w-1.5 h-4",
  past: "w-1.5 h-2",
};

const DOT_SIZE = {
  current: "w-3 h-3",
  recent: "w-1.5 h-1.5",
  past: "w-0.5 h-0.5",
};

const CANDIDATES = [
  {
    id: "c1",
    name: "M. Lindqvist",
    initials: "ML",
    yearsExp: 8,
    location: "Stockholm",
    cells: {
      cap1: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2024-08-01",
          "2025-03-15",
          "2025-11-20",
          "2026-01-10",
          "2026-04-12",
        ],
      },
      cap2: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2023-06-01",
          "2024-02-10",
          "2025-05-15",
          "2025-12-01",
          "2026-03-20",
        ],
      },
      cap3: {
        tier: "extensive",
        evidence: 4,
        evidenceDates: ["2024-01-15", "2024-10-05", "2025-06-20", "2026-02-14"],
      },
      cap4: {
        tier: "extensive",
        evidence: 3,
        evidenceDates: ["2023-09-01", "2024-04-12", "2025-08-30"],
      },
      cap5: {
        tier: "working",
        evidence: 2,
        evidenceDates: ["2022-03-10", "2023-11-15"],
      },
      cap6: {
        tier: "basic",
        evidence: 1,
        evidenceDates: ["2021-05-20"],
      },
      cap7: {
        tier: "working",
        evidence: 3,
        evidenceDates: ["2023-02-10", "2024-09-15", "2025-12-01"],
      },
      cap8: {
        tier: "expert",
        evidence: 4,
        evidenceDates: ["2022-11-05", "2024-03-18", "2025-04-22", "2025-10-30"],
      },
    },
  },
  {
    id: "c2",
    name: "K. Hassan",
    initials: "KH",
    yearsExp: 6,
    location: "Oslo (remote)",
    cells: {
      cap1: {
        tier: "extensive",
        evidence: 4,
        evidenceDates: ["2023-08-01", "2024-09-15", "2025-06-10", "2026-02-20"],
      },
      cap2: {
        tier: "working",
        evidence: 4,
        evidenceDates: ["2022-04-01", "2023-10-15", "2024-12-20", "2025-08-10"],
      },
      cap3: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2023-02-10",
          "2024-05-20",
          "2025-01-15",
          "2025-09-30",
          "2026-04-05",
        ],
      },
      cap4: {
        tier: "working",
        evidence: 3,
        evidenceDates: ["2022-06-20", "2024-01-15", "2025-07-10"],
      },
      cap5: {
        tier: "extensive",
        evidence: 4,
        evidenceDates: ["2024-03-10", "2025-01-20", "2025-08-15", "2026-03-01"],
      },
      cap6: {
        tier: "working",
        evidence: 3,
        evidenceDates: ["2023-05-10", "2024-11-20", "2026-01-15"],
      },
      cap7: {
        tier: "basic",
        evidence: 2,
        evidenceDates: ["2019-08-10", "2020-04-15"],
      },
      cap8: {
        tier: "working",
        evidence: 3,
        evidenceDates: ["2022-10-15", "2024-06-20", "2025-09-30"],
      },
    },
  },
  {
    id: "c3",
    name: "J. Berg",
    initials: "JB",
    yearsExp: 11,
    location: "Stockholm",
    cells: {
      cap1: {
        tier: "expert",
        evidence: 5,
        // Strong but mostly old — was an expert years ago, less active now
        evidenceDates: [
          "2017-04-01",
          "2018-08-15",
          "2019-11-20",
          "2020-06-10",
          "2021-09-12",
        ],
      },
      cap2: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2016-05-10",
          "2018-02-20",
          "2019-07-15",
          "2020-12-01",
          "2022-03-10",
        ],
      },
      cap3: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2019-01-10",
          "2020-08-20",
          "2021-04-15",
          "2022-11-30",
          "2024-02-10",
        ],
      },
      cap4: {
        tier: "expert",
        evidence: 4,
        evidenceDates: ["2018-06-15", "2020-03-10", "2021-09-20", "2023-05-15"],
      },
      cap5: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2019-10-01",
          "2020-05-15",
          "2021-11-20",
          "2022-08-10",
          "2023-12-05",
        ],
      },
      cap6: {
        tier: "extensive",
        evidence: 4,
        evidenceDates: ["2018-03-10", "2019-09-15", "2020-08-20", "2022-04-10"],
      },
      cap7: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2015-06-01",
          "2017-08-15",
          "2019-04-10",
          "2021-10-20",
          "2024-01-15",
        ],
      },
      cap8: {
        tier: "expert",
        evidence: 5,
        evidenceDates: [
          "2020-02-15",
          "2021-08-30",
          "2023-04-10",
          "2024-11-20",
          "2026-02-15",
        ],
      },
    },
  },
  {
    id: "c4",
    name: "A. Sundberg",
    initials: "AS",
    yearsExp: 4,
    location: "Gothenburg",
    cells: {
      cap1: {
        tier: "working",
        evidence: 3,
        evidenceDates: ["2024-08-10", "2025-06-15", "2026-03-20"],
      },
      cap2: {
        tier: "working",
        evidence: 3,
        evidenceDates: ["2024-09-20", "2025-08-10", "2026-04-05"],
      },
      cap3: {
        tier: "working",
        evidence: 3,
        evidenceDates: ["2024-10-15", "2025-10-01", "2026-04-15"],
      },
      cap4: {
        tier: "basic",
        evidence: 2,
        evidenceDates: ["2025-06-20", "2026-01-10"],
      },
      cap5: {
        tier: null,
        evidence: 0,
        evidenceDates: [],
      },
      cap6: {
        tier: null,
        evidence: 0,
        evidenceDates: [],
      },
      cap7: {
        tier: "basic",
        evidence: 1,
        evidenceDates: ["2025-11-15"],
      },
      cap8: {
        tier: "working",
        evidence: 2,
        evidenceDates: ["2024-12-05", "2025-09-10"],
      },
    },
  },
];

// Mock evidence drill-down (per candidate × capability)
const EVIDENCE_DETAIL = {
  "c1-cap5": [
    {
      type: "Employment",
      label: "Senior Backend Engineer @ Klarna",
      duration: "2022-2024",
      detail: "Built payment routing service handling ~3M tx/day",
      verified: true,
    },
    {
      type: "Peer endorsement",
      label: "Endorsed by Anna L. (Engineering Manager)",
      duration: "May 2026",
      detail:
        "Anna worked directly with M. on the payment routing redesign at Klarna.",
      verified: true,
    },
  ],
};

// ────────────────────────────────────────────────────────────
// Helper: composite score per cell
// Recency factor: cell score decays with age of latest evidence.
// This keeps the score honest with the visual encoding —
// a cell with aging evidence renders smaller AND scores lower.

const RECENCY_FACTOR = {
  current: 1.0,
  recent: 0.75,
  past: 0.4,
};

function cellScore(cell) {
  if (!cell || !cell.tier) return 0;
  const tier = TIERS[cell.tier];
  const recency = recencyOfCell(cell);
  return tier.points * (cell.evidence / 5) * RECENCY_FACTOR[recency];
}

function totalCandidateScore(candidate) {
  return Object.values(candidate.cells)
    .map(cellScore)
    .reduce((a, b) => a + b, 0);
}

// ────────────────────────────────────────────────────────────
// Components

function ModeToggle({ mode, onChange }) {
  return (
    <div className="inline-flex items-center bg-gray-100 dark:bg-white/5 rounded-full p-1 text-xs">
      <button
        onClick={() => onChange("list")}
        className={`px-3 py-1 rounded-full font-medium transition ${
          mode === "list"
            ? "bg-white dark:bg-brand-dark-surface text-gray-900 dark:text-white shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
        }`}
      >
        List view
      </button>
      <button
        onClick={() => onChange("capability")}
        className={`px-3 py-1 rounded-full font-medium transition ${
          mode === "capability"
            ? "bg-white dark:bg-brand-dark-surface text-gray-900 dark:text-white shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
        }`}
      >
        Capability view
      </button>
    </div>
  );
}

function CapabilityCell({ cell, isHighest, onClick, selected }) {
  if (!cell || !cell.tier) {
    // Empty state — quietest possible visual.
    // No border, no text, just a muted dash. Reads as absence,
    // not as a competing category in the grid.
    return (
      <button
        onClick={onClick}
        className={`w-full aspect-[7/4] flex items-center justify-center rounded-md transition ${
          selected
            ? "bg-brand-violet/5 ring-1 ring-brand-violet/40 dark:ring-brand-violet-on-dark/40"
            : "bg-gray-50/40 dark:bg-white/[0.02] hover:bg-gray-100/60 dark:hover:bg-white/[0.04]"
        }`}
        aria-label="No claim"
      >
        <span className="text-gray-300 dark:text-white/15 text-sm select-none">
          —
        </span>
      </button>
    );
  }

  const tier = TIERS[cell.tier];
  const score = cellScore(cell);

  // Single brand-violet intensity proportional to composite score.
  // Score range: 0 (empty) → 10 (experienced × 5 evidence).
  // No editorializing colors (no amber for "weak", no stoplight system).
  // The user judges whether a score is a problem; the platform shows data.
  const normalized = score / 10; // 0..1

  // Pick a bg + text combo that maintains contrast at every intensity.
  // High intensity = full gradient (white text). Lower intensity = tinted
  // background with brand-violet text.
  let bg, textColor;
  if (normalized >= 0.85) {
    bg = "bg-brand-violet";
    textColor = "text-white";
  } else if (normalized >= 0.5) {
    bg = "bg-brand-violet/25 dark:bg-brand-violet/35";
    textColor = "text-brand-violet dark:text-brand-violet-on-dark dark:text-brand-violet-soft";
  } else if (normalized >= 0.2) {
    bg = "bg-brand-violet/10 dark:bg-brand-violet/15";
    textColor = "text-brand-violet/90 dark:text-brand-violet-on-dark/90";
  } else {
    bg = "bg-brand-violet/5 dark:bg-brand-violet/[0.07]";
    textColor = "text-gray-700 dark:text-gray-300";
  }

  // Golden border indicates strongest candidate in this capability.
  // Border (not floating star) keeps the indicator self-contained.
  // Guard: empty cells can never be the strongest (defensive check, even
  // though the early return for empty cells means this won't trigger).
  const borderClass =
    isHighest && cell && cell.tier
      ? "border-2 border-yellow-400 dark:border-yellow-500"
      : "border border-gray-100 dark:border-white/5";

  // Compute recency for tier (whole bar uses latest evidence date)
  const cellRecency = recencyOfCell(cell);
  const segmentSizeClass = SEGMENT_SIZE[cellRecency];

  // Compute per-evidence-dot recency
  // evidenceDates is sorted descending (newest first) for display
  const sortedDates = (cell.evidenceDates || []).slice().sort().reverse();

  return (
    <button
      onClick={onClick}
      className={`w-full aspect-[7/4] relative flex items-center justify-center gap-2 rounded-md ${bg} ${borderClass} transition hover:scale-[1.02] hover:shadow-md ${
        selected
          ? "ring-2 ring-brand-violet dark:ring-brand-violet-on-dark ring-offset-1 dark:ring-offset-brand-dark-surface"
          : ""
      }`}
      title={`${tier.label} · ${cell.evidence}/5 evidence · ${cellRecency}`}
    >
      {/* Four-segment tier bar — segment height encodes recency */}
      <div className="flex gap-0.5 items-center">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`${segmentSizeClass} rounded-sm ${
              i <= tier.segments
                ? normalized >= 0.85
                  ? "bg-white"
                  : "bg-current opacity-80"
                : normalized >= 0.85
                  ? "bg-white/25"
                  : "bg-current opacity-15"
            } ${textColor}`}
          />
        ))}
      </div>
      {/* Evidence dots — each dot's size encodes its own recency */}
      <div className="flex gap-0.5 items-center">
        {[1, 2, 3, 4, 5].map((i) => {
          const filled = i <= cell.evidence;
          // Per-dot recency, drawn from sortedDates (newest dot gets newest date)
          const dotRecency = filled
            ? recencyOfDate(sortedDates[i - 1])
            : "current"; // unfilled dots use default size
          const dotSizeClass = DOT_SIZE[dotRecency];
          return (
            <div
              key={i}
              className={`${dotSizeClass} rounded-full ${
                filled
                  ? normalized >= 0.85
                    ? "bg-white/90"
                    : "bg-current opacity-70"
                  : normalized >= 0.85
                    ? "bg-white/25"
                    : "bg-current opacity-15"
              } ${textColor}`}
            />
          );
        })}
      </div>
    </button>
  );
}

function LayerHeader({ label, count }) {
  return (
    <div className="flex items-center gap-3 px-3 py-1.5 mb-1.5 -mx-3">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 dark:via-white/10 to-transparent" />
      <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 dark:via-white/10 to-transparent" />
    </div>
  );
}

function EvidencePopover({ candidate, capability, evidence }) {
  return (
    <div className="mt-3 p-4 bg-brand-violet/5 rounded-xl border border-brand-violet/20 dark:border-brand-violet-on-dark/20">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-violet dark:text-brand-violet-on-dark">
            Evidence detail
          </p>
          <p className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">
            {candidate.name} · {capability.label}
          </p>
        </div>
      </div>

      {evidence && evidence.length > 0 ? (
        <div className="space-y-2.5">
          {evidence.map((e, i) => (
            <div
              key={i}
              className="p-3 bg-white dark:bg-brand-dark-surface rounded-lg border border-gray-100 dark:border-white/5"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {e.verified ? (
                    <div className="w-5 h-5 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-green-600 dark:text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border border-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                    {e.type}
                  </p>
                  <p className="text-xs font-medium text-gray-900 dark:text-white">
                    {e.label}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {e.duration}
                  </p>
                  <p className="text-xs text-gray-700 dark:text-gray-300 mt-1.5 leading-relaxed">
                    {e.detail}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-3 bg-white dark:bg-brand-dark-surface rounded-lg border border-gray-100 dark:border-white/5 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No evidence recorded for this candidate × capability combination.
          </p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
            (Demo: drill-down content is illustrative — only M. Lindqvist ×
            Payment systems has mock evidence loaded.)
          </p>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Main component

export default function CCRMockupV2() {
  const [mode, setMode] = useState("capability");
  const [selectedCell, setSelectedCell] = useState(null);
  const [hiddenCandidates, setHiddenCandidates] = useState(new Set());
  // Separate from hidden state — hide-from-view is local recruiter convenience,
  // include-in-share is an explicit decision about what the client sees.
  // Default: all candidates included; recruiter explicitly excludes if needed.
  const [excludedFromShare, setExcludedFromShare] = useState(new Set());
  const [columnOrder, setColumnOrder] = useState(CANDIDATES.map((c) => c.id));
  const [draggingId, setDraggingId] = useState(null);

  const visibleCandidates = columnOrder
    .map((id) => CANDIDATES.find((c) => c.id === id))
    .filter((c) => c && !hiddenCandidates.has(c.id));

  const toggleHidden = (id) => {
    setHiddenCandidates((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleShareInclude = (id) => {
    setExcludedFromShare((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDragStart = (id) => setDraggingId(id);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetId) => {
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      return;
    }
    setColumnOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(draggingId);
      const to = next.indexOf(targetId);
      next.splice(from, 1);
      next.splice(to, 0, draggingId);
      return next;
    });
    setDraggingId(null);
  };

  // Group capabilities by layer
  const layered = CAPABILITIES.reduce((acc, cap) => {
    if (!acc[cap.layer]) acc[cap.layer] = [];
    acc[cap.layer].push(cap);
    return acc;
  }, {});

  const layerOrder = ["Foundation", "Applied", "Domain", "Experience"];

  // Highest score per capability (across visible candidates only — so hidden
  // candidates can't keep "owning" the star while invisible).
  // Requires positive score; empty cells (score 0) can never win.
  // Ties: ALL candidates at max score share the golden border, not just the
  // first one in column order. The platform should not silently break ties.
  const highestPerCap = {};
  CAPABILITIES.forEach((cap) => {
    let max = 0;
    const maxIds = new Set();
    visibleCandidates.forEach((c) => {
      const cell = c.cells[cap.id];
      if (!cell || !cell.tier) return;
      const s = cellScore(cell);
      if (s > max) {
        max = s;
        maxIds.clear();
        maxIds.add(c.id);
      } else if (s === max && s > 0) {
        maxIds.add(c.id);
      }
    });
    highestPerCap[cap.id] = maxIds;
  });

  const handleCellClick = (candidateId, capabilityId) => {
    const key = `${candidateId}-${capabilityId}`;
    setSelectedCell(selectedCell === key ? null : key);
  };

  const selectedDetail = selectedCell
    ? {
        candidate: CANDIDATES.find((c) => c.id === selectedCell.split("-")[0]),
        capability: CAPABILITIES.find(
          (cap) => cap.id === selectedCell.split("-")[1],
        ),
        evidence: EVIDENCE_DETAIL[selectedCell],
      }
    : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100 font-sans">
      {/* Simulated nav */}
      <nav className="border-b border-gray-100 dark:border-white/10 bg-white dark:bg-brand-dark-surface">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="font-extrabold text-base bg-brand-violet bg-clip-text text-transparent">
              joborigo
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Agency · PerfectHire Global
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a className="text-xs text-gray-900 dark:text-white font-medium">
              Positions
            </a>
            <a className="text-xs text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark">
              Candidates
            </a>
            <a className="text-xs text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark">
              Clients
            </a>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 pt-5 pb-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Positions /{" "}
          <span className="text-gray-900 dark:text-white font-medium">
            {ROLE.title} · {ROLE.client}
          </span>{" "}
          / Capability map
        </p>
      </div>

      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">
              {ROLE.title}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {ROLE.client} · Capability template:{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {ROLE.template}
              </span>{" "}
              · {visibleCandidates.length} of {CANDIDATES.length} candidates
              shown
            </p>
          </div>
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      {/* Capability mode */}
      {mode === "capability" && (
        <div className="max-w-7xl mx-auto px-6 pb-12">
          {/* Legend */}
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] text-gray-500 dark:text-gray-400">
              <span className="font-semibold uppercase tracking-wider">
                Tier (bar fill)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 items-center">
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-300 dark:bg-gray-600" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-300 dark:bg-gray-600" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-300 dark:bg-gray-600" />
                </span>
                Basic
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 items-center">
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-300 dark:bg-gray-600" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-300 dark:bg-gray-600" />
                </span>
                Working
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 items-center">
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-300 dark:bg-gray-600" />
                </span>
                Extensive
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 items-center">
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1 h-3 rounded-sm bg-gray-500 dark:bg-gray-400" />
                </span>
                Expert
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] text-gray-500 dark:text-gray-400">
              <span className="font-semibold uppercase tracking-wider">
                Recency (size)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 items-center">
                  <span className="inline-block w-1 h-6 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-3 h-3 rounded-full bg-gray-500 dark:bg-gray-400" />
                </span>
                Current ({"<"}1y)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 items-center">
                  <span className="inline-block w-1 h-4 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-500 dark:bg-gray-400" />
                </span>
                Recent (1–5y)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5 items-center">
                  <span className="inline-block w-1 h-2 rounded-sm bg-gray-500 dark:bg-gray-400" />
                  <span className="inline-block w-0.5 h-0.5 rounded-full bg-gray-500 dark:bg-gray-400" />
                </span>
                Past (5y+)
              </span>
              <span className="text-gray-400 dark:text-gray-500 italic">
                Tier bar = latest evidence age · Each dot = its own age
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] text-gray-500 dark:text-gray-400">
              <span className="font-semibold uppercase tracking-wider">
                Other
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span
                      key={i}
                      className="inline-block w-1 h-1 rounded-full bg-gray-400"
                    />
                  ))}
                </span>
                Evidence count · 0–5
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded border-2 border-yellow-400 dark:border-yellow-500" />
                Strongest in this capability
              </span>
            </div>
          </div>

          {/* The grid */}
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 overflow-hidden">
            {/* Candidate header row */}
            <div
              className="grid border-b border-gray-100 dark:border-white/5"
              style={{
                gridTemplateColumns: `220px repeat(${visibleCandidates.length}, minmax(0, 1fr))`,
              }}
            >
              <div className="px-4 py-3 flex items-end">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
                  Capability ↓ / Candidate →
                </span>
              </div>
              {visibleCandidates.map((c) => {
                const total = totalCandidateScore(c).toFixed(1);
                const maxPossible = CAPABILITIES.length * 10;
                const pct = (
                  (totalCandidateScore(c) / maxPossible) *
                  100
                ).toFixed(0);
                const isDragging = draggingId === c.id;
                const isExcluded = excludedFromShare.has(c.id);
                return (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={() => handleDragStart(c.id)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(c.id)}
                    className={`relative px-2 py-3 border-l border-gray-100 dark:border-white/5 flex flex-col items-center text-center transition cursor-move group ${
                      isDragging ? "opacity-40" : ""
                    } ${
                      isExcluded ? "bg-gray-50/60 dark:bg-white/[0.015]" : ""
                    }`}
                  >
                    {/* Action buttons cluster */}
                    <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleShareInclude(c.id);
                        }}
                        className={`w-5 h-5 rounded flex items-center justify-center transition ${
                          isExcluded
                            ? "text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
                            : "text-brand-violet dark:text-brand-violet-on-dark hover:text-brand-violet-lift"
                        }`}
                        title={
                          isExcluded
                            ? "Include in client share"
                            : "Exclude from client share"
                        }
                      >
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          {isExcluded ? (
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                              clipRule="evenodd"
                            />
                          ) : (
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          )}
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHidden(c.id);
                        }}
                        className="w-5 h-5 rounded flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 dark:hover:text-white"
                        title="Hide from my view (does not affect client share)"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="absolute top-1 left-1 opacity-30 group-hover:opacity-60 transition">
                      <svg
                        className="w-3 h-3 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M7 2a2 2 0 11-.001 4.001A2 2 0 017 2zm0 6a2 2 0 11-.001 4.001A2 2 0 017 8zm0 6a2 2 0 11-.001 4.001A2 2 0 017 14zm6-12a2 2 0 11-.001 4.001A2 2 0 0113 2zm0 6a2 2 0 11-.001 4.001A2 2 0 0113 8zm0 6a2 2 0 11-.001 4.001A2 2 0 0113 14z" />
                      </svg>
                    </div>

                    <div
                      className={`w-9 h-9 rounded-full bg-brand-violet flex items-center justify-center text-white font-bold text-xs mb-1.5 mt-1 ${
                        isExcluded ? "opacity-40" : ""
                      }`}
                    >
                      {c.initials}
                    </div>
                    <p
                      className={`text-xs font-semibold ${
                        isExcluded
                          ? "text-gray-400 dark:text-gray-500 line-through"
                          : "text-gray-900 dark:text-white"
                      }`}
                    >
                      {c.name}
                    </p>
                    <p
                      className={`text-[10px] mt-0.5 ${
                        isExcluded
                          ? "text-gray-400 dark:text-gray-500"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {c.yearsExp}y · {c.location}
                    </p>
                    <div
                      className={`mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        isExcluded
                          ? "bg-gray-100/60 dark:bg-white/[0.03] text-gray-400 dark:text-gray-500"
                          : "bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {total} / {maxPossible} ({pct}%)
                    </div>
                    {isExcluded && (
                      <div className="mt-1 text-[9px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold">
                        excluded from share
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Capability rows, grouped by layer */}
            <div className="px-3 py-3">
              {layerOrder.map((layer) => {
                if (!layered[layer]) return null;
                return (
                  <div key={layer} className="mb-3 last:mb-0">
                    <LayerHeader label={layer} count={layered[layer].length} />
                    {layered[layer].map((cap) => (
                      <div
                        key={cap.id}
                        className="grid items-center"
                        style={{
                          gridTemplateColumns: `220px repeat(${visibleCandidates.length}, minmax(0, 1fr))`,
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <div className="px-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {cap.label}
                          </p>
                        </div>
                        {visibleCandidates.map((c) => (
                          <CapabilityCell
                            key={`${c.id}-${cap.id}`}
                            cell={c.cells[cap.id]}
                            isHighest={highestPerCap[cap.id]?.has(c.id)}
                            selected={selectedCell === `${c.id}-${cap.id}`}
                            onClick={() => handleCellClick(c.id, cap.id)}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Evidence drill-down */}
          {selectedDetail && (
            <EvidencePopover
              candidate={selectedDetail.candidate}
              capability={selectedDetail.capability}
              evidence={selectedDetail.evidence}
            />
          )}

          {/* Hidden candidates restoration */}
          {hiddenCandidates.size > 0 && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-white/[0.03] rounded-xl border border-gray-100 dark:border-white/5 flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                Hidden ({hiddenCandidates.size}):
              </span>
              {[...hiddenCandidates].map((id) => {
                const c = CANDIDATES.find((x) => x.id === id);
                return (
                  <button
                    key={id}
                    onClick={() => toggleHidden(id)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white dark:bg-brand-dark-surface border border-gray-200 dark:border-white/10 text-xs text-gray-700 dark:text-gray-300 hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                  >
                    <span>{c.name}</span>
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </button>
                );
              })}
            </div>
          )}

          {/* Action bar */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 p-4 bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10">
            <div className="text-xs text-gray-500 dark:text-gray-400 max-w-2xl">
              Drag column headers to reorder. Hover a column for actions: the
              check icon controls inclusion in the client share, the eye icon
              hides the column from your view only. Joborigo does not pre-rank —
              the client decides the final order.
            </div>
            <div className="flex items-center gap-2">
              <button className="text-xs font-medium px-4 py-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition">
                Edit capability template
              </button>
              <button className="text-xs font-medium px-4 py-2 rounded-lg bg-brand-violet text-white hover:opacity-90 transition">
                Share with client ({CANDIDATES.length - excludedFromShare.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List mode placeholder */}
      {mode === "list" && (
        <div className="max-w-7xl mx-auto px-6 pb-12">
          <div className="p-8 bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              List view shows today's agency candidate list — per-candidate
              detail, trajectory, evidence ordered by recency. Not redrawn in
              this mockup (already exists in production).
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              The same underlying candidate evidence powers both views.
            </p>
          </div>
        </div>
      )}

      {/* Design note */}
      <footer className="max-w-7xl mx-auto px-6 pb-10">
        <div className="p-4 bg-brand-violet/5 dark:bg-brand-violet/10 rounded-xl border border-brand-violet/10 dark:border-brand-violet-on-dark/10 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
          <p className="font-semibold text-brand-violet dark:text-brand-violet-on-dark mb-1">
            Design reference v2.5 — capability map with temporal recency
          </p>
          <p>
            The capability template belongs to the role, not the candidate.
            Candidates are projected onto the template. Each cell encodes three
            dimensions: <span className="font-semibold">tier</span>{" "}
            (four-segment bar fill: Basic → Working → Extensive → Expert),{" "}
            <span className="font-semibold">evidence strength</span> (five dots,
            count of supporting evidence), and{" "}
            <span className="font-semibold">recency</span> (size of each
            element: current under 1 year, recent 1-5 years, past 5+ years).
            Composite score per cell = tier_points × (evidence / 5) ×
            recency_factor (1.0 / 0.75 / 0.4 for current / recent / past).
            Recency factors into the score so visual and math agree — an aging
            cell renders smaller AND scores lower.
          </p>
          <p className="mt-2">
            <span className="font-semibold">
              Tier-bar size = latest evidence age.
            </span>{" "}
            One size for the whole bar, based on the newest evidence item. A
            capability that hasn't been exercised recently shows a smaller bar
            even if the tier claim is still Expert — visually communicating that
            the claim is on shaky ground.
          </p>
          <p className="mt-2">
            <span className="font-semibold">
              Each evidence dot's size = its own age.
            </span>{" "}
            A cell with three recent endorsements and two old ones shows three
            medium dots and two small dots. The mix is honest; the recruiter
            sees at a glance "mostly recent, some legacy."
          </p>
          <p className="mt-2">
            <span className="font-semibold">
              No pre-ranking, no tie-breaking.
            </span>{" "}
            Candidates appear as columns, not a vertical list. When multiple
            candidates share the top score for a capability, all tied cells get
            the golden border. The platform does not silently pick a winner
            among equals.
          </p>
          <p className="mt-2">
            <span className="font-semibold">Hide ≠ Exclude.</span> Two separate
            controls: eye icon hides the column from the recruiter's view (local
            convenience); check icon toggles inclusion in the client share.
            Excluded candidates are visually muted so the recruiter sees exactly
            what the client will see.
          </p>
        </div>
      </footer>
    </div>
  );
}
